import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../../../api'
import { ORANGE, TEAL, VERT, eur } from './paniersProd'

/**
 * Modale d'IMPACT (proto v7.3, palier 3 / ADR-059).
 *
 * Quand le producteur veut descendre sous ce qui est deja reserve, ou retirer
 * un produit deja commande, on ne refuse plus sechement : on lui montre ce que
 * ca touche — quelles commandes, quels clients, combien sera rembourse — et il
 * confirme l'envoi d'une DEMANDE. Manrina valide avant toute application.
 */
function ImpactModal({ tourId, ligne, quantiteVoulue, onClose, onEnvoye, onToast }) {
  const [impact, setImpact] = useState(null)
  const [motif, setMotif] = useState('')
  const [busy, setBusy] = useState(false)

  // Ce qui ne pourra PAS etre servi = la part du RESERVE qui passe a la trappe.
  // On ne part pas de la dispo : baisser de 10 a 3 quand 4 sont reserves ne
  // lese qu'1 unite de client, pas 7.
  const manquant =
    Math.round((Number(ligne.vendu) - Number(quantiteVoulue)) * 100) / 100
  const retrait = Number(quantiteVoulue) === 0

  useEffect(() => {
    api.producer.getTourImpact(tourId, ligne.tourOfferId, manquant)
      .then(setImpact)
      .catch(() => setImpact(null))
  }, [tourId, ligne.tourOfferId, manquant])

  const envoyer = () => {
    setBusy(true)
    api.producer.demanderAjustement(tourId, ligne.tourOfferId, manquant, motif || undefined)
      .then(() => {
        onEnvoye?.()
        onClose()
      })
      .catch((e) => onToast?.(e.message || `L'envoi de la demande a échoué.`))
      .finally(() => setBusy(false))
  }

  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[75] flex items-end justify-center"
      style={{ background: 'rgba(4,25,24,0.55)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-dark w-full overflow-y-auto overscroll-contain"
        style={{ maxWidth: 448, maxHeight: '82dvh', borderRadius: '26px 26px 0 0', padding: '20px 18px 26px', color: 'rgb(var(--c-creme))' }}
      >
        <span className="block text-[28px] mb-1">⚠️</span>
        <p className="font-serif text-[21px] leading-tight">
          {retrait ? 'Retirer un produit déjà réservé' : 'Vous passez sous ce qui est réservé'}
        </p>
        <p className="text-[12.5px] mt-1.5 leading-relaxed" style={{ color: 'rgba(247,240,234,0.75)' }}>
          {ligne.produit} — {retrait ? 'ce produit a' : `il manquera ${manquant} ${ligne.unite} par rapport à ce qui a`} déjà été commandé.
          Des clients sont concernés : Manrina doit valider avant que quoi que ce soit ne change.
        </p>

        {impact === null ? (
          <div className="h-24 rounded-2xl mt-3.5 animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
        ) : impact.nbCommandes === 0 ? (
          /* Aucune commande ajustable : on l'explique au lieu de laisser un
             bouton mort (bug remonte par Enzo le 23 juil). */
          <div className="rounded-[18px] mt-3.5" style={{ background: 'rgba(255,255,255,0.07)', padding: '13px 15px' }}>
            <p className="text-[12.5px] leading-relaxed" style={{ color: 'rgba(247,240,234,0.85)' }}>
              Aucune commande ne peut être ajustée sur ce produit pour le moment.
              Soit les commandes concernées ont déjà été réglées au producteur
              (la fenêtre est passée), soit elles ont été annulées.
            </p>
            <p className="text-[11.5px] mt-2" style={{ color: 'rgba(247,240,234,0.6)' }}>
              Contactez Manrina si vous pensez qu'il y a une erreur.
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-[18px] mt-3.5" style={{ background: 'rgba(255,255,255,0.07)', padding: '13px 15px' }}>
              <p className="text-[10.5px] font-extrabold uppercase" style={{ letterSpacing: 1.6, color: 'rgba(247,240,234,0.6)' }}>
                Commandes touchées ({impact.nbCommandes})
              </p>
              <div className="flex flex-col gap-1.5 mt-2">
                {impact.commandes.map((c) => (
                  <div key={c.visibleId} className="flex justify-between items-baseline gap-2">
                    <span className="text-[12px] truncate" style={{ color: 'rgba(247,240,234,0.85)' }}>
                      {c.client || c.visibleId} · −{c.quantiteRetiree} {ligne.unite}
                    </span>
                    <span className="text-[12.5px] font-bold whitespace-nowrap" style={{ color: c.paye === false ? 'rgba(247,240,234,0.55)' : '#ffc9a3' }}>
                      {c.paye === false ? 'non réglée' : eur(c.montantRembourse)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-baseline pt-2 mt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                <span className="text-[12.5px] font-bold">Total remboursé aux clients</span>
                <span className="font-serif text-[17px]" style={{ color: '#ffc9a3' }}>{eur(impact.montantTotal)}</span>
              </div>
            </div>

            <label className="block mt-3.5">
              <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: 1.4, color: 'rgba(247,240,234,0.6)' }}>
                Que s'est-il passé ? (facultatif)
              </span>
              <textarea
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Ex. : la pluie a abîmé une partie de la récolte"
                className="w-full mt-1.5 rounded-[14px] text-[13px]"
                style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgb(var(--c-creme))', resize: 'none' }}
              />
            </label>

            <button
              onClick={envoyer}
              disabled={busy}
              className="w-full mt-3 rounded-2xl text-[13.5px] font-extrabold disabled:opacity-50"
              style={{ padding: 14, border: 'none', background: ORANGE, color: '#04302f' }}
            >
              {busy ? 'Envoi…' : 'Envoyer la demande à Manrina'}
            </button>
          </>
        )}

        <button
          onClick={onClose}
          className="w-full mt-2 rounded-2xl text-[13px] font-bold"
          style={{ padding: 12, border: 'none', background: 'rgba(255,255,255,0.1)', color: 'rgb(var(--c-creme))' }}
        >
          Annuler
        </button>
        <p className="text-[10.5px] leading-relaxed mt-2.5 text-center" style={{ color: 'rgba(247,240,234,0.5)' }}>
          🦊 Rien n'est modifié tant que Manrina n'a pas validé. Les clients concernés seront remboursés.
        </p>
      </div>
    </div>,
    document.body,
  )
}

export default ImpactModal
